import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, CheckCircle2, Radio } from "lucide-react";
import { API } from "@/lib/stream";

const TEAM_SIZES = ["1-50", "51-200", "201-1000", "1000+"];
const PAIN_POINTS = [
  "Onboarding & training staff",
  "Compliance & fair housing",
  "Retention & engagement",
  "Documentation overload",
  "All of the above",
];

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const SCORE_COLOR = (s) => (s >= 85 ? "text-[#10B981]" : s >= 70 ? "text-[#F59E0B]" : "text-slate-500");

export default function LeadCaptureCRM() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", work_email: "", team_size: TEAM_SIZES[0], pain_point: PAIN_POINTS[0], notes: "" });
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/leads`);
      const data = await res.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch { /* feed stays as-is */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ company_name: "", contact_name: "", work_email: "", team_size: TEAM_SIZES[0], pain_point: PAIN_POINTS[0], notes: "" });
        await load();
        setTimeout(() => setSuccess(false), 4000);
      }
    } catch { /* ignore */ }
    setBusy(false);
  };

  const inputCls =
    "w-full min-w-0 bg-white/5 rounded-2xl border-2 border-white/10 px-4 py-3 text-sm font-medium text-white outline-none focus:border-[#FF5722] transition-colors placeholder:text-slate-500 [&>option]:bg-[#0F2440] [&>option]:text-white";

  return (
    <section id="crm-demo" className="relative py-24 sm:py-32 bg-[#0B1B33] border-y border-white/10 overflow-hidden">
      <div className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full bg-[#2563EB]/20 blur-3xl" />
      <div className="absolute bottom-0 -right-32 w-[24rem] h-[24rem] rounded-full bg-[#FF5722]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#38BDF8]">Live CRM · Built In</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3 text-white">
            Every hello becomes a pipeline entry.
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed mt-4">
            Register your company and watch it land in the Opera360 AI pipeline in real time —
            scored, routed, and ready for a demo call.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
          <motion.form
            data-testid="crm-lead-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl border-2 border-white/10 bg-[#0F2440]/90 backdrop-blur p-7 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)] space-y-4"
          >
            <div className="flex items-center gap-3 pb-1">
              <span className="inline-flex p-2.5 rounded-2xl bg-[#FF5722]/15 text-[#FF5722]"><Building2 className="w-5 h-5" /></span>
              <div className="font-display font-bold text-lg text-white">Register your company</div>
            </div>
            <input data-testid="crm-company-name-input" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company / portfolio name" className={inputCls} />
            <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your name" className={inputCls} />
            <input data-testid="crm-work-email-input" required type="email" value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} placeholder="Work email" className={inputCls} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select data-testid="crm-team-size-select" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} className={inputCls}>
                {TEAM_SIZES.map((s) => <option key={s} value={s}>{s} people</option>)}
              </select>
              <select data-testid="crm-pain-point-select" value={form.pain_point} onChange={(e) => setForm({ ...form, pain_point: e.target.value })} className={inputCls}>
                {PAIN_POINTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything else we should know? (optional)" className={`${inputCls} resize-none`} />
            <button
              data-testid="crm-submit-button"
              type="submit"
              disabled={busy}
              className="w-full px-6 py-4 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-[#38BDF8] hover:text-[#0B1B33] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? "Registering…" : "Put my company in the pipeline"}
            </button>
            {success && (
              <motion.div
                data-testid="crm-success-badge"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 justify-center text-sm font-bold text-[#10B981]"
              >
                <CheckCircle2 className="w-4 h-4" /> You're in! Watch the live feed →
              </motion.div>
            )}
          </motion.form>

          <motion.div
            data-testid="crm-live-feed-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-[#0F2440]/90 backdrop-blur border-2 border-white/10 p-7 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.65)]"
          >
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-2 text-white font-display font-bold">
                <Radio className="w-4 h-4 text-[#FF5722] animate-pulse-dot" /> Live CRM Lead Feed
              </div>
              <span className="text-xs font-bold text-slate-400">{total} companies in pipeline</span>
            </div>
            <div className="mt-4 space-y-3 max-h-[26rem] overflow-y-auto chat-scroll pr-1">
              {leads.length === 0 && (
                <div className="text-slate-500 text-sm py-10 text-center">No leads yet — be the first company in the pipeline.</div>
              )}
              {leads.map((l) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#FF5722] flex items-center justify-center font-display font-black text-white text-sm shrink-0">
                    {l.company_name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold text-sm truncate">{l.company_name}</div>
                    <div className="text-slate-400 text-xs truncate">{l.pain_point} · {l.team_size} people · {l.work_email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display font-black text-lg ${SCORE_COLOR(l.score)}`}>{l.score}</div>
                    <div className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">{timeAgo(l.created_at)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
